/**
 * SubscriptionPoolPanel — Annual Cost (data/content, hosting, server
 * management) pool of a finalized PO. Mirrors the AMC pool panel but draws
 * from the separate subscription pool; no project-completion gate.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CloudArrowUp, Receipt } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { PoolTile, CountTile, CreateAmcFromPoDialog, LinkedSchedulesList } from "./AmcPoolPanel";

export default function SubscriptionPoolPanel({ po, onChanged }) {
    const bs = po?.billing_status || {};
    const [preview, setPreview] = useState(null);
    const [err, setErr] = useState(null);
    const has = !!bs.has_subscription_value;

    useEffect(() => {
        if (!po?.id || !has) return;
        let cancelled = false;
        api.get(`/billing/amc/po/${po.id}/amc-creation-preview?kind=subscription`)
            .then((r) => { if (!cancelled) setPreview(r.data); })
            .catch((e) => { if (!cancelled) setErr(formatApiError(e)); });
        return () => { cancelled = true; };
    }, [po?.id, has, bs.subscription_allocated_amount]);

    if (!has) return null;
    const available = bs.subscription_available_to_allocate_amount ?? 0;
    const cur = po?.currency;
    return (
        <div className="reedos-card p-5" data-testid="subscription-pool-panel">
            <div className="flex items-center gap-2 mb-3">
                <CloudArrowUp size={14} className="text-sky-700" weight="duotone" />
                <div className="reedos-eyebrow">Annual Cost · Subscription Pool</div>
                {bs.subscription_over_billed_amount > 0 && (
                    <span className="ml-2 rounded px-2 py-0.5 text-[10px] font-bold border bg-rose-50 text-rose-700 border-rose-200" data-testid="subscription-over-billed-badge">
                        Over-billed
                    </span>
                )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <PoolTile label="Contract Value" value={bs.subscription_po_total} currency={cur} testid="sub-pool-contract"
                    subtitle={`Excl GST: ${Number(bs.subscription_po_excl_gst || 0).toLocaleString("en-IN")}`} />
                <PoolTile label="Allocated" value={bs.subscription_allocated_amount} currency={cur} tone="amber" testid="sub-pool-allocated" />
                <PoolTile label="Available to Allocate" value={available} currency={cur} tone="emerald" testid="sub-pool-available" />
                <CountTile label="Schedules" count={bs.linked_subscription_schedule_count || 0} testid="sub-pool-count" />
                <PoolTile label="Invoiced" value={bs.subscription_invoiced_amount} currency={cur} tone="indigo" testid="sub-pool-invoiced" />
                <PoolTile label="Outstanding" value={bs.subscription_outstanding_amount} currency={cur}
                    tone={bs.subscription_outstanding_amount > 0 ? "rose" : "emerald"} testid="sub-pool-outstanding" />
            </div>
            <p className="mt-3 text-[11px] text-slate-500">
                Subscriptions start on the agreed service commencement date — no project-completion gate. Billed separately from the AMC pool.
            </p>
            <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                        <Receipt size={12} className="text-slate-600" />
                        <div className="reedos-eyebrow text-[10px]">Linked Subscription Schedules</div>
                        {(bs.linked_subscription_schedules || []).length > 0 && (
                            <Link to={`/amc?po_id=${po.id}`} className="text-[10px] text-slate-500 hover:text-slate-900 hover:underline ml-2" data-testid="sub-pool-view-all-link">
                                View in AMC list →
                            </Link>
                        )}
                    </div>
                    <CreateAmcFromPoDialog
                        po={po} preview={preview} onCreated={onChanged} kind="subscription"
                        disabled={available <= 0.01 || !preview}
                        disabledReason={available <= 0.01 ? "Subscription pool fully allocated" : ""}
                    />
                </div>
                <LinkedSchedulesList schedules={bs.linked_subscription_schedules} currency={cur} emptyText="No subscription schedules linked yet" />
                {err && <p className="text-[11px] text-rose-600 mt-2">{err}</p>}
            </div>
        </div>
    );
}
